import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Search, Shield, CheckCircle2, XCircle, Clock, Ban, RotateCcw, Pencil, Trash2, X,
  Loader2, FileText, AlertTriangle, Car, Building2, User, Package, CalendarDays, StickyNote,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const inputCls = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#C6A15B]/60 focus:outline-none";
const labelCls = "block text-xs font-medium text-slate-400 mb-1.5";
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const Skeleton = ({ className = '' }) => <div className={`animate-pulse bg-white/[0.05] rounded-lg ${className}`} />;

// Calendar-year arithmetic from a base date, leap-day safe (Feb 29 -> Feb 28 on non-leap years).
const addYears = (isoDate, years) => {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return '';
  const t = new Date(y + years, m - 1, d);
  if (t.getDate() !== d) t.setDate(0);
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};
const DURATION_PRESETS = [
  { k: '3y', label: '3 Years', yrs: 3 }, { k: '5y', label: '5 Years', yrs: 5 },
  { k: '10y', label: '10 Years', yrs: 10 }, { k: 'custom', label: 'Custom', yrs: null },
];
const Group = ({ title, icon: Icon, children }) => (
  <div className="bg-black/20 border border-white/[0.06] rounded-xl p-4">
    <div className="flex items-center gap-2 mb-3"><Icon className="w-4 h-4 text-[#C6A15B]" /><h4 className="text-xs font-semibold uppercase tracking-wide text-slate-300">{title}</h4></div>
    {children}
  </div>
);
const DField = ({ label, value, mono }) => (
  <div><div className="text-[11px] text-slate-500">{label}</div><div className={`text-sm text-slate-200 ${mono ? 'font-mono' : ''}`}>{value || '—'}</div></div>
);

const ST = {
  pending: { label: 'Under Review', color: '#F59E0B', icon: Clock },
  approved: { label: 'Approved', color: '#10B981', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: '#EF4444', icon: XCircle },
  voided: { label: 'Voided', color: '#64748B', icon: Ban },
};
const stOf = (s) => ST[(s || '').toLowerCase()] || { label: s || '—', color: '#64748B', icon: Clock };
const Pill = ({ s }) => { const m = stOf(s); return <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full" style={{ background: `${m.color}1f`, color: m.color }}><m.icon className="w-3 h-3" />{m.label}</span>; };

// Derived expiration lifecycle (separate from approval status; never mutates it).
const lifecycleOf = (w) => {
  if ((w.status || '').toLowerCase() !== 'approved') return null;
  if (!w.warranty_end_date) return { key: 'not_set', label: 'Not Set', color: '#64748B' };
  const days = Math.floor((new Date(w.warranty_end_date) - new Date()) / 86400000);
  if (days < 0) return { key: 'expired', label: 'Expired', color: '#EF4444' };
  if (days <= 30) return { key: 'expiring', label: 'Expiring Soon', color: '#F59E0B' };
  return { key: 'active', label: 'Active', color: '#10B981' };
};
const LifeBadge = ({ w }) => { const m = lifecycleOf(w); if (!m) return null; return <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full mt-0.5" style={{ background: `${m.color}1f`, color: m.color }} data-testid={`lifecycle-${w.warranty_id}`}>{m.label}</span>; };
const LIFE_FILTERS = [
  { k: 'all', label: 'All' }, { k: 'active', label: 'Active' }, { k: 'expiring', label: 'Expiring Soon' },
  { k: 'expired', label: 'Expired' }, { k: 'not_set', label: 'Not Set' },
];

const Modal = ({ children, onClose, max = 'max-w-lg', testid }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid={testid}>
    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
    <div className={`relative w-full ${max} bg-[#14171B] border border-white/[0.08] rounded-2xl max-h-[92vh] overflow-y-auto`}>{children}</div>
  </div>
);

export const AdminWarrantyCenter = () => {
  const [tab, setTab] = useState('pending');
  const [warranties, setWarranties] = useState(null);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [q, setQ] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [sel, setSel] = useState(null);
  const [reviewMode, setReviewMode] = useState(null); // 'approve' | 'reject'
  const [notes, setNotes] = useState('');
  const [expDate, setExpDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [preset, setPreset] = useState('custom');
  const [lifeFilter, setLifeFilter] = useState('all');
  const [customCredits, setCustomCredits] = useState({});
  const [editForm, setEditForm] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchWarranties = async () => {
    setWarranties(null);
    try {
      const status = tab === 'all' ? '' : tab;
      const r = await axios.get(`${API}/api/admin/warranties${status ? `?status=${status}` : ''}`);
      setWarranties(r.data.warranties || []);
      setCounts(r.data.status_counts || { pending: 0, approved: 0, rejected: 0 });
    } catch (e) { setWarranties([]); toast.error('Error loading certificates'); }
  };
  useEffect(() => { fetchWarranties(); /* eslint-disable-next-line */ }, [tab]);

  const runSearch = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 2) { setSearchResults(null); return; }
    try { const r = await axios.get(`${API}/api/admin/warranties/search?q=${encodeURIComponent(q.trim())}`); setSearchResults(r.data || []); }
    catch (e) { toast.error('Search failed'); setSearchResults([]); }
  };

  const rows = searchResults !== null ? searchResults : (warranties || []);
  const displayedRows = lifeFilter === 'all' ? rows : rows.filter((w) => (lifecycleOf(w)?.key) === lifeFilter);

  const openReview = (w, mode) => {
    setSel(w); setReviewMode(mode); setCustomCredits({});
    const inst = w.installation_date ? w.installation_date.split('T')[0] : '';
    setNotes(mode === 'approve' ? (w.admin_notes || '') : '');
    setStartDate(w.warranty_start_date ? w.warranty_start_date.split('T')[0] : inst);
    setExpDate(w.warranty_end_date ? w.warranty_end_date.split('T')[0] : '');
    setPreset('custom');
  };
  // Presets compute expiration from the certificate's Installation Date (calendar years).
  const applyPreset = (p) => {
    setPreset(p);
    if (p === 'custom') return;
    const yrs = DURATION_PRESETS.find((x) => x.k === p)?.yrs;
    const base = (sel?.installation_date || '').split('T')[0] || startDate;
    if (base && yrs) setExpDate(addYears(base, yrs));
  };
  const submitReview = async () => {
    if (reviewMode === 'approve' && !expDate) { toast.error('Please set the warranty expiration date before approving this warranty.'); return; }
    setBusy(true);
    try {
      const payload = { action: reviewMode, admin_notes: notes };
      if (reviewMode === 'approve') {
        payload.warranty_end_date = expDate;
        if (startDate) payload.warranty_start_date = startDate;
        const reviewItems = (sel.coverage_items || []).filter((i) => i.requires_review);
        if (reviewItems.length) {
          const cc = {};
          for (const it of reviewItems) { const v = parseInt(customCredits[it.area_key], 10); if (!v || v < 0) { toast.error(`Assign credits for "${it.area_name_snapshot || it.area_key}"`); setBusy(false); return; } cc[it.area_key] = v; }
          payload.custom_credits = cc;
        }
      }
      await axios.put(`${API}/api/admin/warranties/${sel.warranty_id}/approve`, payload);
      toast.success(reviewMode === 'approve' ? 'Certificate approved' : 'Certificate rejected');
      setSel(null); setReviewMode(null); setSearchResults(null); setQ(''); fetchWarranties();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const openEdit = (w) => setEditForm({
    warranty_id: w.warranty_id, client_name: w.client_name || '', vehicle_make: w.vehicle_make || '', vehicle_model: w.vehicle_model || '',
    vehicle_year: w.vehicle_year || '', vin_number: w.vin_number || '', vehicle_mileage: w.vehicle_mileage || '',
    installation_date: w.installation_date?.split('T')[0] || '', installation_location: w.installation_location || '',
    additional_notes: w.additional_notes || '', admin_notes: w.admin_notes || '', status: w.status || '',
    warranty_start_date: w.warranty_start_date?.split('T')[0] || '', warranty_end_date: w.warranty_end_date?.split('T')[0] || '',
  });
  const submitEdit = async () => {
    setBusy(true);
    try { const { warranty_id, ...body } = editForm; await axios.put(`${API}/api/admin/warranties/${warranty_id}`, body); toast.success('Certificate updated'); setEditForm(null); fetchWarranties(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    finally { setBusy(false); }
  };

  const submitVoid = async () => {
    if (!voidReason.trim()) { toast.error('A reason is required to void'); return; }
    setBusy(true);
    try { const r = await axios.post(`${API}/api/admin/warranties/${voidTarget.warranty_id}/void`, { reason: voidReason.trim() }); toast.success(`Voided. ${r.data.capacity_returned ?? 0} credits returned.`); setVoidTarget(null); setVoidReason(''); setSearchResults(null); setQ(''); fetchWarranties(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Void failed'); }
    finally { setBusy(false); }
  };
  const submitRestore = async () => {
    setBusy(true);
    try { await axios.post(`${API}/api/admin/warranties/${restoreTarget.warranty_id}/restore`, {}); toast.success('Certificate restored'); setRestoreTarget(null); setSearchResults(null); setQ(''); fetchWarranties(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Restore blocked'); }
    finally { setBusy(false); }
  };
  const del = async (w) => {
    if (!window.confirm(`Delete certificate ${w.serial_number}? This cannot be undone.`)) return;
    try { await axios.delete(`${API}/api/admin/warranties/${w.warranty_id}`); toast.success('Certificate deleted'); fetchWarranties(); if (searchResults) setSearchResults(searchResults.filter((x) => x.warranty_id !== w.warranty_id)); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const TABS = [
    { k: 'pending', label: 'Under Review', n: counts.pending },
    { k: 'approved', label: 'Approved', n: counts.approved },
    { k: 'rejected', label: 'Rejected', n: counts.rejected },
    { k: 'voided', label: 'Voided' },
    { k: 'all', label: 'All' },
  ];

  return (
    <div className="space-y-6" data-testid="admin-warranty-center">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-white">Warranty Management</h2>
        <p className="text-slate-400 text-sm mt-1">Review, approve, void and manage installation certificates.</p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Summary label="Under Review" value={counts.pending} color="#F59E0B" icon={Clock} />
        <Summary label="Approved" value={counts.approved} color="#10B981" icon={CheckCircle2} />
        <Summary label="Rejected" value={counts.rejected} color="#EF4444" icon={XCircle} />
      </div>

      <form onSubmit={runSearch} className="flex gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => { setQ(e.target.value); if (!e.target.value.trim()) setSearchResults(null); }} placeholder="Search roll #, serial / certificate #, customer or dealer name, VIN..." className={`${inputCls} pl-9`} data-testid="admin-warranty-search" />
        </div>
        <button type="submit" className="text-sm px-4 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 text-slate-200">Search</button>
        {searchResults !== null && <button type="button" onClick={() => { setQ(''); setSearchResults(null); }} className="text-sm px-4 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 text-slate-200">Clear</button>}
      </form>

      {searchResults === null && (
        <div className="flex gap-2 flex-wrap border-b border-white/[0.06] pb-3">
          {TABS.map((tb) => (
            <button key={tb.k} onClick={() => setTab(tb.k)} data-testid={`admin-warranty-tab-${tb.k}`}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm transition-colors ${tab === tb.k ? 'bg-[#C6A15B]/12 text-[#E7CB85] font-medium' : 'text-slate-400 hover:text-slate-100 hover:bg-white/[0.04]'}`}>
              {tb.label}{typeof tb.n === 'number' ? <span className="text-xs bg-white/[0.08] rounded-full px-1.5">{tb.n}</span> : null}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-wide text-slate-500 mr-1">Expiration</span>
        {LIFE_FILTERS.map((lf) => (
          <button key={lf.k} onClick={() => setLifeFilter(lf.k)} data-testid={`expiration-filter-${lf.k}`}
            className={`px-2.5 py-1 rounded-md text-xs transition-colors ${lifeFilter === lf.k ? 'bg-[#C6A15B] text-black font-medium' : 'bg-white/[0.04] text-slate-400 hover:text-slate-100'}`}>
            {lf.label}
          </button>
        ))}
      </div>

      {warranties === null && searchResults === null ? <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16" />)}</div>
        : displayedRows.length === 0 ? (
          <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl py-16 text-center">
            <Shield className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-300 font-medium">No certificates found</p>
          </div>
        ) : (
          <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[1040px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-white/[0.06]">
                  <th className="px-4 py-3 font-medium">Serial</th>
                  <th className="px-4 py-3 font-medium">Dealer / Installation Center</th>
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Vehicle</th>
                  <th className="px-4 py-3 font-medium">Coverage</th>
                  <th className="px-4 py-3 font-medium">Roll</th>
                  <th className="px-4 py-3 font-medium text-right">Credits</th>
                  <th className="px-4 py-3 font-medium">Expiration</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedRows.map((w) => {
                  const s = (w.status || '').toLowerCase();
                  return (
                    <tr key={w.warranty_id} className="border-b border-white/[0.04] last:border-0 hover:bg-white/[0.03]" data-testid={`admin-warranty-row-${w.warranty_id}`}>
                      <td className="px-4 py-3 text-slate-100 font-mono text-xs">{w.serial_number}</td>
                      <td className="px-4 py-3 text-slate-300 max-w-[200px] truncate" data-testid={`dealer-name-${w.warranty_id}`}>{w.dealer_name || w.user_company || '—'}</td>
                      <td className="px-4 py-3 text-slate-300">{w.client_name}</td>
                      <td className="px-4 py-3 text-slate-400">{[w.vehicle_year, w.vehicle_make, w.vehicle_model].filter(Boolean).join(' ') || '—'}</td>
                      <td className="px-4 py-3 text-slate-400 max-w-[180px] truncate">{w.coverage_summary || w.installation_location || '—'}</td>
                      <td className="px-4 py-3 text-[#C6A15B] font-mono text-xs">{w.roll_number || '—'}</td>
                      <td className="px-4 py-3 text-right text-slate-300">{w.capacity_consumed ?? '—'}</td>
                      <td className="px-4 py-3" data-testid={`expiration-${w.warranty_id}`}>
                        <div className="flex flex-col">
                          <span className={w.warranty_end_date ? 'text-slate-200' : 'text-slate-500'}>{w.warranty_end_date ? fmtDate(w.warranty_end_date) : 'Not Set'}</span>
                          <LifeBadge w={w} />
                        </div>
                      </td>
                      <td className="px-4 py-3"><Pill s={w.status} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          {s === 'pending' && <>
                            <IconBtn title="Approve" onClick={() => openReview(w, 'approve')} testid={`approve-btn-${w.warranty_id}`} accent><CheckCircle2 className="w-4 h-4" /></IconBtn>
                            <IconBtn title="Reject" onClick={() => openReview(w, 'reject')} testid={`reject-btn-${w.warranty_id}`} danger><XCircle className="w-4 h-4" /></IconBtn>
                          </>}
                          {s === 'approved' && <IconBtn title="Void" onClick={() => { setVoidTarget(w); setVoidReason(''); }} testid={`void-btn-${w.warranty_id}`} danger><Ban className="w-4 h-4" /></IconBtn>}
                          {s === 'voided' && <IconBtn title="Restore" onClick={() => setRestoreTarget(w)} testid={`restore-btn-${w.warranty_id}`}><RotateCcw className="w-4 h-4" /></IconBtn>}
                          <IconBtn title="Edit" onClick={() => openEdit(w)} testid={`edit-warranty-btn-${w.warranty_id}`}><Pencil className="w-4 h-4" /></IconBtn>
                          <IconBtn title="Delete" onClick={() => del(w)} danger testid={`delete-warranty-btn-${w.warranty_id}`}><Trash2 className="w-4 h-4" /></IconBtn>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      {/* Review & Approve (unified) / Reject */}
      {sel && reviewMode === 'approve' && (
        <Modal onClose={() => { setSel(null); setReviewMode(null); }} max="max-w-3xl" testid="review-dialog">
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08] sticky top-0 bg-[#14171B] z-10">
            <h3 className="text-white font-semibold flex items-center gap-2"><Shield className="w-5 h-5 text-[#C6A15B]" /> Review &amp; Approve Certificate</h3>
            <button onClick={() => { setSel(null); setReviewMode(null); }} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-6 space-y-4">
            <Group title="Certificate / Dealer" icon={Building2}>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <DField label="Certificate #" value={sel.serial_number} mono />
                <DField label="Dealer / Installation Center" value={sel.dealer_name || sel.user_company} />
                <DField label="Roll / Source" value={sel.roll_number} mono />
              </div>
            </Group>

            <Group title="Customer & Vehicle" icon={User}>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <DField label="Customer" value={sel.client_name} />
                <DField label="Vehicle" value={[sel.vehicle_year, sel.vehicle_make, sel.vehicle_model].filter(Boolean).join(' ')} />
                <DField label="VIN" value={sel.vin_number} mono />
                <DField label="Mileage" value={sel.vehicle_mileage} />
                <DField label="Installation Date" value={fmtDate(sel.installation_date)} />
                <DField label="Installation Area" value={sel.installation_location || sel.coverage_type} />
              </div>
            </Group>

            <Group title="Installation Coverage" icon={Package}>
              <div className="text-sm text-slate-300 mb-2">{sel.coverage_summary || '—'}</div>
              {(sel.coverage_items || []).length > 0 ? (
                <div className="space-y-1.5" data-testid="review-coverage-items">
                  {(sel.coverage_items || []).map((it, i) => (
                    <div key={i} className="flex items-center justify-between text-sm bg-black/30 rounded-lg px-3 py-1.5">
                      <span className="text-slate-200">{it.area_name_snapshot || it.custom_description || it.area_key}{it.side ? ` · ${it.side}` : ''}{it.quantity > 1 ? ` ×${it.quantity}` : ''}</span>
                      <span className="text-[#C6A15B] text-xs">{it.total_credit_consumed ?? it.credit_per_unit_snapshot ?? '—'} credits{it.requires_review ? ' · review' : ''}</span>
                    </div>
                  ))}
                </div>
              ) : <div className="text-slate-500 text-sm">No itemized coverage recorded.</div>}
              <div className="text-xs text-slate-500 mt-2">Total credits consumed: <span className="text-slate-300">{sel.capacity_consumed ?? '—'}</span></div>
              {sel.additional_notes ? <div className="text-xs text-slate-400 mt-2">Installer notes: {sel.additional_notes}</div> : null}
            </Group>

            <Group title="Warranty Dates & Duration" icon={CalendarDays}>
              <div className="flex flex-wrap gap-2 mb-3">
                {DURATION_PRESETS.map((p) => (
                  <button key={p.k} onClick={() => applyPreset(p.k)} data-testid={`duration-preset-${p.k}`}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${preset === p.k ? 'bg-[#C6A15B] text-black font-medium' : 'bg-white/[0.05] border border-white/10 text-slate-300 hover:bg-white/[0.08]'}`}>
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Warranty Start Date</label>
                  <input type="date" className={inputCls} value={startDate} data-testid="review-start"
                    onChange={(e) => { setStartDate(e.target.value); }} />
                </div>
                <div>
                  <label className={labelCls}>Warranty Expiration Date *</label>
                  <input type="date" className={inputCls} value={expDate} data-testid="review-expiration"
                    onChange={(e) => { setExpDate(e.target.value); setPreset('custom'); }} />
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">Presets calculate expiration from the Installation Date. You set the exact dates — dealers cannot choose them.</p>
            </Group>

            {(sel.coverage_items || []).filter((i) => i.requires_review).length > 0 && (
              <Group title="Assign Credits (review areas)" icon={AlertTriangle}>
                <div className="space-y-2">
                  {(sel.coverage_items || []).filter((i) => i.requires_review).map((it) => (
                    <div key={it.area_key} className="flex items-center gap-2">
                      <span className="text-sm text-slate-300 flex-1">{it.area_name_snapshot || it.custom_description || it.area_key}</span>
                      <input type="number" min="0" className={`${inputCls} w-24`} value={customCredits[it.area_key] || ''} onChange={(e) => setCustomCredits({ ...customCredits, [it.area_key]: e.target.value })} data-testid={`review-credits-${it.area_key}`} />
                    </div>
                  ))}
                </div>
              </Group>
            )}

            <Group title="Admin Notes" icon={StickyNote}>
              <textarea rows={3} className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="review-notes" placeholder="Optional notes for this certificate" />
            </Group>

            <div className="flex gap-3 sticky bottom-0 bg-[#14171B] pt-2">
              <button onClick={() => { setSel(null); setReviewMode(null); }} className="flex-1 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 text-slate-200 text-sm">Cancel</button>
              <button onClick={submitReview} disabled={busy} data-testid="review-confirm" className="flex-1 py-2.5 rounded-lg font-medium text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60 bg-[#C6A15B] text-black">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Approve Certificate
              </button>
            </div>
          </div>
        </Modal>
      )}

      {sel && reviewMode === 'reject' && (
        <Modal onClose={() => { setSel(null); setReviewMode(null); }} testid="review-dialog">
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08]">
            <h3 className="text-white font-semibold">Reject Certificate</h3>
            <button onClick={() => { setSel(null); setReviewMode(null); }} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-6 space-y-4">
            <div className="text-sm text-slate-400">{sel.serial_number} · {sel.client_name}</div>
            <div>
              <label className={labelCls}>Admin Notes (reason shown to customer)</label>
              <textarea rows={3} className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="review-notes" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setSel(null); setReviewMode(null); }} className="flex-1 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 text-slate-200 text-sm">Cancel</button>
              <button onClick={submitReview} disabled={busy} data-testid="review-confirm" className="flex-1 py-2.5 rounded-lg font-medium text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60 bg-red-500/90 text-white">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Reject
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit */}
      {editForm && (
        <Modal onClose={() => setEditForm(null)} max="max-w-2xl" testid="edit-warranty-dialog">
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08] sticky top-0 bg-[#14171B] z-10">
            <h3 className="text-white font-semibold flex items-center gap-2"><Car className="w-5 h-5 text-[#C6A15B]" /> Edit Certificate</h3>
            <button onClick={() => setEditForm(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-6 space-y-4">
            <div><label className={labelCls}>Client Name</label><input className={inputCls} value={editForm.client_name} onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })} /></div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div><label className={labelCls}>Make</label><input className={inputCls} value={editForm.vehicle_make} onChange={(e) => setEditForm({ ...editForm, vehicle_make: e.target.value })} /></div>
              <div><label className={labelCls}>Model</label><input className={inputCls} value={editForm.vehicle_model} onChange={(e) => setEditForm({ ...editForm, vehicle_model: e.target.value })} /></div>
              <div><label className={labelCls}>Year</label><input className={inputCls} value={editForm.vehicle_year} onChange={(e) => setEditForm({ ...editForm, vehicle_year: e.target.value })} /></div>
              <div><label className={labelCls}>Mileage</label><input className={inputCls} value={editForm.vehicle_mileage} onChange={(e) => setEditForm({ ...editForm, vehicle_mileage: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelCls}>VIN</label><input className={inputCls} value={editForm.vin_number} onChange={(e) => setEditForm({ ...editForm, vin_number: e.target.value })} /></div>
              <div><label className={labelCls}>Installation Date</label><input type="date" className={inputCls} value={editForm.installation_date} onChange={(e) => setEditForm({ ...editForm, installation_date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelCls}>Warranty Start</label><input type="date" className={inputCls} value={editForm.warranty_start_date} onChange={(e) => setEditForm({ ...editForm, warranty_start_date: e.target.value })} /></div>
              <div><label className={labelCls}>Warranty End</label><input type="date" className={inputCls} value={editForm.warranty_end_date} onChange={(e) => setEditForm({ ...editForm, warranty_end_date: e.target.value })} /></div>
            </div>
            <div><label className={labelCls}>Admin Notes</label><textarea rows={2} className={inputCls} value={editForm.admin_notes} onChange={(e) => setEditForm({ ...editForm, admin_notes: e.target.value })} /></div>
            <div className="flex gap-3">
              <button onClick={() => setEditForm(null)} className="flex-1 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 text-slate-200 text-sm">Cancel</button>
              <button onClick={submitEdit} disabled={busy} data-testid="edit-warranty-save" className="flex-1 py-2.5 rounded-lg bg-[#C6A15B] text-black font-medium text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Save</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Void */}
      {voidTarget && (
        <Modal onClose={() => setVoidTarget(null)} testid="void-dialog">
          <div className="p-6 space-y-4">
            <h3 className="text-white font-semibold flex items-center gap-2"><Ban className="w-5 h-5 text-red-400" /> Void Certificate?</h3>
            <p className="text-slate-400 text-sm">Voiding <span className="text-slate-200 font-mono">{voidTarget.serial_number}</span> may restore its consumed credits to roll <span className="text-[#C6A15B] font-mono">{voidTarget.roll_number}</span>.</p>
            <div><label className={labelCls}>Reason *</label><textarea rows={3} className={inputCls} value={voidReason} onChange={(e) => setVoidReason(e.target.value)} data-testid="void-reason" /></div>
            <div className="flex gap-3">
              <button onClick={() => setVoidTarget(null)} className="flex-1 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 text-slate-200 text-sm">Cancel</button>
              <button onClick={submitVoid} disabled={busy} data-testid="void-confirm" className="flex-1 py-2.5 rounded-lg bg-red-500/90 text-white font-medium text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />} Void Certificate</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Restore */}
      {restoreTarget && (
        <Modal onClose={() => setRestoreTarget(null)} testid="restore-dialog">
          <div className="p-6 space-y-4">
            <h3 className="text-white font-semibold flex items-center gap-2"><RotateCcw className="w-5 h-5 text-[#C6A15B]" /> Restore Certificate?</h3>
            <p className="text-slate-400 text-sm">Restore <span className="text-slate-200 font-mono">{restoreTarget.serial_number}</span>? This re-consumes its coverage if capacity allows.</p>
            <div className="flex gap-3">
              <button onClick={() => setRestoreTarget(null)} className="flex-1 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 text-slate-200 text-sm">Cancel</button>
              <button onClick={submitRestore} disabled={busy} data-testid="restore-confirm" className="flex-1 py-2.5 rounded-lg bg-[#C6A15B] text-black font-medium text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Restore</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

const Summary = ({ label, value, color, icon: Icon }) => (
  <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl p-5">
    <div className="flex items-center justify-between">
      <span className="text-slate-400 text-sm">{label}</span>
      <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${color}1f`, color }}><Icon className="w-4 h-4" /></span>
    </div>
    <div className="text-2xl font-bold text-white mt-2">{value}</div>
  </div>
);
const IconBtn = ({ children, onClick, title, danger, accent, testid }) => (
  <button title={title} onClick={onClick} data-testid={testid}
    className={`w-8 h-8 inline-flex items-center justify-center rounded-lg border transition-colors ${danger ? 'border-red-500/20 text-red-400 hover:bg-red-500/10' : accent ? 'border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/10' : 'border-white/10 text-slate-300 hover:bg-white/[0.08]'}`}>{children}</button>
);

export default AdminWarrantyCenter;
